import Vue from 'vue';
import { store } from '../store';
import moment from 'moment';
import { toNumber } from './formatNumber';
import DATA from './DATA';
import { EventBus } from './eventBus';

const NAME = "NETWORK-EVENT-BUS";

/**
 * Catalogue of every SSE stream the server can expose.
 * `values` is the event_key used everywhere in this bus (server_payload.network_listening_events,
 * EVENTS map keys, EVENT_SOURCES registry keys, etc).
 */
const NETWORK_LISTENING_EVENTS = [
    { values: "mongodb",                  path: 'mongo/dbEvents',           name: "MongoDB" },
    { values: "nfc",                      path: 'NFC/events',               name: "NFC" },
    { values: "sage_invoice",             path: 'Invoice/events',           name: "Sage Invoice" },
    { values: "sage_inventory_transfer",  path: 'WarehouseTransfer/events', name: "Sage Inventory Transfer" },
    { values: "sage_customer",            path: 'CustomerMaster/events',    name: "Sage Customer" },
];

// Accepts either the canonical `values` key ("sage_invoice") or the display
// `name` ("Sage Invoice"), case-insensitively, and always returns the config
// with its canonical `values` - callers should key EVENTS / registries off
// config.values so it stays consistent no matter which form was passed in.
function resolveEventConfig(input) {
    if (!input) return null;
    const needle = String(input).trim().toLowerCase();
    return NETWORK_LISTENING_EVENTS.find(e =>
        e.values.toLowerCase() === needle || e.name.toLowerCase() === needle
    ) || null;
}

// Live EventSource + interval handles are NOT put in Vue's reactive `data`.
// They hold browser objects / timers that Vue 2's reactivity walker would
// otherwise try (and fail) to observe, and nothing in the UI needs to bind
// to them directly - only the plain-object status (LAN / EVENTS / SERVERS)
// below does.
const EVENT_SOURCE_REGISTRY = {}; // `${event_key}::${link}` -> EventSource
const LAN_SOURCES = {};           // link -> EventSource (heartbeat stream)

// How long START_LAN waits for the initial connection to open before
// resolving as "not live" (the browser keeps retrying in the background
// regardless - this only bounds how long START_SERVER waits for a result).
const LAN_CHECK_TIMEOUT_MS = 4000;

export const NetworkEventBus = new Vue({
    data() {
        return {
            // code -> { code, status, link, network_listening_events }
            SERVERS: {},

            // link -> boolean (is this server reachable right now)
            LAN: {},

            // link -> latest SystemStatusResult payload from the heartbeat stream
            // ({ timestampUtc, checks: [...], specs: {...} })
            SERVER_INFO: {},

            // event_key -> { active: boolean, active_links: [link, ...] }
            EVENTS: {},

            // link -> category ('sage_invoice' | 'sage_inventory_transfer' | ...)
            //      -> { loading, lan, page, pageSize, processed, totalCount, items }
            // Lets the same server track independent pagination/loading state
            // per Sage entity type, instead of one shared loader for everything.
            SAGE_LOADER: {}
        }
    },
    created() {

    },
    mounted() {

    },
    computed: {
        Servers() {
            return this.SERVERS
        },
        Lan() {
            return this.LAN
        },
        ServerInfo() {
            return this.SERVER_INFO
        },
        Events() {
            return this.EVENTS
        },
        SageLoader() {
            return this.SAGE_LOADER
        },
        selectedCompany() {
            const re = store?.getters.getSelectedCompany
            return re
        },
    },
    methods: {

        // =====================================================================
        // 1. START_SERVER - top level entry point.
        //    server_payload = { code, status, link, network_listening_events: [event_key, ...] }
        //    Reconciles against the previously known state for `code`:
        //      - status false            -> tear everything down for that server
        //      - link changed            -> tear down the old link's LAN + events, start fresh on the new link
        //      - new event_keys added    -> START_EVENT for the new ones only
        //      - event_keys removed      -> CLOSE_EVENT for the ones that dropped off
        // =====================================================================
        // Returns a result object rather than void:
        //   { success, code, link, event, live, events, reason? }
        // `event` is one of 'connected' | 'error' | 'offline'. `events` is a
        // snapshot like { nfc: true, sage_invoice: false, ... } - whether each
        // requested stream is currently active. On 'connected' / 'error' the
        // same object is also emitted on EventBus as 'ON_SERVER_STATUS' so
        // anything not awaiting the call can still react to it.
        //
        // NOTE: EventSource connections open asynchronously, so right after
        // START_EVENT is called a stream may not have flipped `active` yet -
        // the `events` snapshot reflects state at return time, not a guarantee
        // every stream is up. Watch NetworkEventBus.Events (or ON_LAN) for the
        // live picture as each stream actually opens.
        async START_SERVER(server_payload) {
            try {
                const { code, status, link, network_listening_events = [] } = server_payload || {};
                if (!code) {
                    console.warn('START_SERVER: server_payload.code is required');
                    return { success: false, event: 'error', reason: 'missing_code' };
                }

                // Accept event names in any form (values key, display name,
                // mixed case) and normalize to the canonical `values` key used
                // everywhere internally (EVENTS map, EVENT_SOURCE_REGISTRY, etc).
                const canonicalEvents = this.CANONICALIZE_EVENT_KEYS(network_listening_events);

                const prev = this.SERVERS[code] || null;
                const linkChanged = !!prev && prev.link !== link;

                // Server turned off, or its link changed -> tear down whatever was
                // previously running for this server before doing anything else.
                if (prev && prev.status && (linkChanged || !status)) {
                    this.CLOSE_SERVER(prev);
                }

                if (!status) {
                    this.$set(this.SERVERS, code, { code, status, link, network_listening_events: canonicalEvents });
                    this.PUSH_STORE_UPDATE();
                    return { success: false, code, link, event: 'offline' };
                }

                if (!link) {
                    console.warn(`START_SERVER: server ${code} is marked on but has no link`);
                    return { success: false, code, link, event: 'error', reason: 'missing_link' };
                }

                // Track raw connectivity for the link itself, and wait for the
                // first check so we know whether it actually connected.
                const live = await this.START_LAN(link);

                // Diff event_keys so we don't blindly restart streams that are
                // already open and unchanged when this is called again (e.g. on a
                // status heartbeat) with the same link + event list.
                const prevEvents = (prev && prev.status && !linkChanged) ? (prev.network_listening_events || []) : [];
                const toStart = canonicalEvents.filter(k => !prevEvents.includes(k));
                const toStop = prevEvents.filter(k => !canonicalEvents.includes(k));

                toStop.forEach(event_key => this.CLOSE_EVENT(event_key, link));
                toStart.forEach(event_key => this.START_EVENT(event_key, link));

                this.$set(this.SERVERS, code, { code, status, link, network_listening_events: canonicalEvents });
                this.PUSH_STORE_UPDATE();

                // Snapshot of each requested event's current active state,
                // e.g. { nfc: true, mongodb: false, sage_invoice: true, ... }
                const events = {};
                canonicalEvents.forEach(key => { events[key] = !!this.EVENTS[key]?.active; });

                const connectedEvent = {
                    code,
                    link,
                    event: live ? 'connected' : 'error',
                    live,
                    events,
                    timestamp: Date.now()
                };
                EventBus.$emit('ON_SERVER_STATUS', connectedEvent);
                store.dispatch('lan_event', connectedEvent);

                return { success: live, ...connectedEvent };

            } catch (error) {
                this.logError(error, 'START_SERVER');
                return { success: false, code: server_payload?.code, link: server_payload?.link, event: 'error' };
            }
        },

        // Normalizes a list of event keys/names to their canonical `values` form,
        // dropping anything that doesn't match a known NETWORK_LISTENING_EVENTS entry.
        CANONICALIZE_EVENT_KEYS(keys = []) {
            return keys
                .map(k => resolveEventConfig(k))
                .filter(Boolean)
                .map(c => c.values);
        },

        // Convenience wrapper for turning a server off by code.
        STOP_SERVER(code) {
            const server = this.SERVERS[code];
            if (!server) return;
            this.START_SERVER({ ...server, status: false });
        },

        // Tears down every event stream + the LAN check tied to a server record.
        CLOSE_SERVER(server) {
            try {
                if (!server) return;
                const { link, network_listening_events = [] } = server;
                network_listening_events.forEach(event_key => this.CLOSE_EVENT(event_key, link));
                this.CLOSE_LAN(link);
            } catch (error) {
                this.logError(error, 'CLOSE_SERVER');
            }
        },

        // =====================================================================
        // 2. START_LAN - lightweight, always-on connectivity tracking for a link.
        //    Opens a persistent SSE connection to the server's status heartbeat
        //    (`api/status/heartbeat`) instead of polling. LAN[link] flips on
        //    onopen/onerror, and the browser handles reconnection natively -
        //    no manual retry/backoff bookkeeping needed on this side. Each
        //    message carries the server's SystemStatusResult snapshot
        //    (checks + specs), which is parsed into SERVER_INFO[link].
        // =====================================================================
        async START_LAN(link) {
            try {
                if (!link) return false;

                // Idempotent: replace any existing stream for this link.
                this.CLOSE_LAN(link);

                return await new Promise((resolve) => {
                    let settled = false;
                    const settle = (live) => {
                        if (settled) return;
                        settled = true;
                        resolve(live);
                    };

                    const source = new EventSource(link + "status/heartbeat");
                    LAN_SOURCES[link] = source;

                    source.onopen = () => {
                        this.$set(this.LAN, link, true);
                        EventBus.$emit('ON_LAN', { lan_event: 'open', link });
                        this.PUSH_STORE_UPDATE();
                        settle(true);
                    };

                    source.onmessage = (event) => {
                        try {
                            const status = JSON.parse(event.data);
                            this.$set(this.SERVER_INFO, link, status);
                            EventBus.$emit('ON_SERVER_INFO', { link, status });
                            this.PUSH_STORE_UPDATE();
                        } catch (err) {
                            this.logError(err, 'START_LAN_PARSE');
                        }
                    };

                    source.onerror = () => {
                        this.$set(this.LAN, link, false);
                        EventBus.$emit('ON_LAN', { lan_event: 'error', link });
                        this.PUSH_STORE_UPDATE();
                        // Only affects the promise this first time - the browser
                        // keeps retrying the connection under the hood regardless.
                        settle(false);
                    };

                    // Don't let START_SERVER hang forever waiting on a dead link.
                    setTimeout(() => settle(false), LAN_CHECK_TIMEOUT_MS);
                });

            } catch (error) {
                this.logError(error, 'START_LAN');
                return false;
            }
        },

        CLOSE_LAN(link) {
            try {
                if (!link) return;
                if (LAN_SOURCES[link]) {
                    LAN_SOURCES[link].close();
                    delete LAN_SOURCES[link];
                }
                this.$set(this.LAN, link, false);
                this.$delete(this.SERVER_INFO, link);
                this.PUSH_STORE_UPDATE();
            } catch (error) {
                this.logError(error, 'CLOSE_LAN');
            }
        },

        // Pushes the full state maps to the store as a set, rather than
        // per-item deltas - keeps the store's copy exactly in sync with
        // what the bus itself holds.
        PUSH_STORE_UPDATE() {
            store.dispatch('lan_sync', { name: "LAN", data: this.LAN });
            store.dispatch('lan_sync', { name: "SERVER_INFO", data: this.SERVER_INFO });
            store.dispatch('lan_sync', { name: "SERVERS", data: this.SERVERS });
            store.dispatch('lan_sync', { name: "EVENTS", data: this.EVENTS });
            store.dispatch('lan_sync', { name: "SAGE_LOADER", data: this.SAGE_LOADER });
        },

        IsLanLive(link) {
            return !!this.LAN[link];
        },

        // =====================================================================
        // 3. START_EVENT - opens the SSE stream for one event_key on one link,
        //    wires up the shared onopen/onmessage/onerror handlers, and keeps
        //    EVENTS[event_key] = { active, active_links } in sync.
        // =====================================================================
        START_EVENT(event_key, basic_link) {
            try {
                const config = resolveEventConfig(event_key);
                if (!config) {
                    console.warn(`START_EVENT: unknown event_key "${event_key}"`);
                    return;
                }
                if (!basic_link) return;

                const key = config.values; // canonical - use this everywhere below

                // Idempotent: replace any existing stream for this exact key+link pair.
                this.CLOSE_EVENT(key, basic_link);

                if (!this.EVENTS[key]) {
                    this.$set(this.EVENTS, key, { active: false, active_links: [] });
                }

                const source = new EventSource(basic_link + config.path);
                EVENT_SOURCE_REGISTRY[`${key}::${basic_link}`] = source;

                source.onopen = (event) => {
                    if (!this.EVENTS[key].active_links.includes(basic_link)) {
                        this.EVENTS[key].active_links.push(basic_link);
                    }
                    this.EVENTS[key].active = true;
                    this.PUSH_STORE_UPDATE();
                    this.updateStatus(basic_link, key, 'open', event);
                    this.handleOnOpenLogic(key, basic_link);
                };

                source.onmessage = (event) => {
                    const response = JSON.parse(event.data);
                    this.updateStatus(basic_link, key, 'message', event, response);
                    this.handleOnMessageLogic(key, response, basic_link);
                };

                source.onerror = (event) => {
                    this.EVENTS[key].active_links = this.EVENTS[key].active_links.filter(l => l !== basic_link);
                    this.EVENTS[key].active = this.EVENTS[key].active_links.length > 0;
                    this.PUSH_STORE_UPDATE();
                    this.updateStatus(basic_link, key, 'error', event);
                };

            } catch (error) {
                this.logError(error, `START_EVENT_${event_key}`);
            }
        },

        CLOSE_EVENT(event_key, link) {
            try {
                const config = resolveEventConfig(event_key);
                const key = config ? config.values : event_key;

                const sourceKey = `${key}::${link}`;
                if (EVENT_SOURCE_REGISTRY[sourceKey]) {
                    EVENT_SOURCE_REGISTRY[sourceKey].close();
                    delete EVENT_SOURCE_REGISTRY[sourceKey];
                }
                if (this.EVENTS[key]) {
                    this.EVENTS[key].active_links = this.EVENTS[key].active_links.filter(l => l !== link);
                    this.EVENTS[key].active = this.EVENTS[key].active_links.length > 0;
                    this.PUSH_STORE_UPDATE();
                }
                EventBus.$emit('ON_LAN', { lan_event: "CLOSE", link, category: key });
            } catch (error) {
                this.logError(error, 'CLOSE_EVENT');
            }
        },

        IsEventActive(event_key) {
            const config = resolveEventConfig(event_key);
            const key = config ? config.values : event_key;
            return !!this.EVENTS[key]?.active;
        },

        // Returns SAGE_LOADER[link][category], creating it with sane defaults
        // on first access so callers never have to null-check it.
        GET_SAGE_LOADER(link, category) {
            if (!this.SAGE_LOADER[link]) this.$set(this.SAGE_LOADER, link, {});
            if (!this.SAGE_LOADER[link][category]) {
                this.$set(this.SAGE_LOADER[link], category, {
                    loading: false,
                    lan: link,
                    page: 0,
                    pageSize: 0,
                    processed: false,
                    totalCount: 1,
                    items: null,
                });
            }
            return this.SAGE_LOADER[link][category];
        },

        // Merges `patch` into SAGE_LOADER[link][category] and pushes the
        // updated map to the store.
        SET_SAGE_LOADER(link, category, patch) {
            const current = this.GET_SAGE_LOADER(link, category);
            this.$set(this.SAGE_LOADER[link], category, { ...current, ...patch });
            this.PUSH_STORE_UPDATE();
            return this.SAGE_LOADER[link][category];
        },

        /**
         * CENTRALIZED LOGIC HANDLERS
         * event_key matches NETWORK_LISTENING_EVENTS[].values
         */
        async handleOnOpenLogic(event_key, link) {
            if (event_key === 'sage_invoice') {
                this.DOWNLOAD_SAGE_INVOICES({ lan: link, page: 1, pageSize: 25, processed: false });
            }
            if (event_key === 'sage_inventory_transfer') {
                this.DOWNLOAD_SAGE_TRANSFERS({ lan: link, page: 1, pageSize: 25, processed: false });
            }
            if (event_key === 'sage_customer') {
                await this.DOWNLOAD_SAGE_CUSTOMERS({ lan: link });
            }
        },

        // Mirrors handleOnOpenLogic - same three DOWNLOAD_SAGE_* calls - but a
        // message means "data changed upstream", not "start fresh", so instead
        // of resetting to page 1 / default pageSize it re-fetches using
        // whatever pagination/filter the UI is currently on, read from
        // SAGE_LOADER.
        async handleOnMessageLogic(event_key, response, link) {
            if (event_key === 'sage_invoice') {
                const loader = this.GET_SAGE_LOADER(link, 'sage_invoice');
                this.DOWNLOAD_SAGE_INVOICES({
                    lan: link,
                    page: loader.page || 1,
                    pageSize: loader.pageSize || 25,
                    processed: loader.processed
                });
            }
            if (event_key === 'sage_inventory_transfer') {
                const loader = this.GET_SAGE_LOADER(link, 'sage_inventory_transfer');
                this.DOWNLOAD_SAGE_TRANSFERS({
                    lan: link,
                    page: loader.page || 1,
                    pageSize: loader.pageSize || 25,
                    processed: loader.processed
                });
            }
            if (event_key === 'sage_customer') {
                await this.DOWNLOAD_SAGE_CUSTOMERS({ lan: link });
            }
        },

        /**
         * BUS EMITTER - fires for every open/message/error on any event stream.
         */
        updateStatus(link, category, lan_event, event, response = null) {
            const payload = {
                link,
                lan_event,
                category,
                event,
                response,
                action: response?.Action,
                data: response?.Data
            };

            console.log(payload,'payload.......');
            

            const busEvent = response?.Action === "nfc" ? "ON_NFC" : "ON_LAN";
            EventBus.$emit(busEvent, payload);
            store.dispatch('lan_event', payload);
        },

        async ON_UPDATE_CUSTOMER_WITH_SAGE(sageCustomers) {
            try {
                const path = this.COMPANY_LINK(DATA.PATHS.customers, true)
                // 1. Validation
                if (!Array.isArray(sageCustomers) || sageCustomers.length === 0) return;

                // 2. Get current full list from Store to maintain UI state
                const currentCustomers = store.getters.getCustomers || [];
                const customerMap = new Map(currentCustomers.map(c => [c.code, c]));

                // This array will ONLY hold the customers that need a Firebase update
                const firebaseUpdates = [];

                sageCustomers.forEach(sage => {
                    const code = sage?.customerNumber;
                    if (!code) return;

                    // Get existing local data so we don't lose local-only fields
                    const existing = customerMap.get(code) || {};

                    // 3. Build the unified object (Local UI version)
                    const updatedCustomer = {
                        ...existing,
                        code: code,
                        key: code,
                        name: sage.customerName || existing.name || '',
                        phone_number: sage.phoneNumber || existing.phone_number || '',
                        email_address: sage.email || existing.email_address || '',
                        customer_group_code: sage.groupCode || '',
                        customer_group_name: sage.customerGroupName || '',
                        source: 'SAGE_SYNC',
                        sync_date: new Date().toISOString()
                    };

                    // Update the Map (This updates the full list for the local UI)
                    customerMap.set(code, updatedCustomer);

                    // 4. Queue ONLY this Sage customer for Firebase
                    firebaseUpdates.push({
                        path: `${path}${code}`,
                        ...updatedCustomer
                    });
                });

                // STEP A: Update Local Store (Full list: existing + new Sage data)
                store.commit('setItems', {
                    name: 'CUSTOMERS',
                    data: Array.from(customerMap.values())
                });

                // STEP B: Update Firebase (ONLY the new/updated Sage customers)
                if (firebaseUpdates.length > 0) {
                    await store.dispatch('fi_update2', {
                        action: 'SYNC_SAGE_ONLY',
                        multiple: true,
                        data: firebaseUpdates,
                        no_frag: false
                    });
                }

            } catch (error) {
                this.error({
                    error: error,
                    from: 'ON_UPDATE_CUSTOMER_WITH_SAGE',
                    page: NAME
                });
            }
        },
        async DOWNLOAD_SAGE_INVOICES(payload) {
            const link = payload.lan;
            const category = 'sage_invoice';
            try {
                this.SET_SAGE_LOADER(link, category, { loading: true });
                const res = await store.dispatch('http_get_sage_items',
                    {
                        link: link + "Invoice/paged",
                        page: payload.page,
                        pageSize: payload.pageSize,
                        processed: payload.processed,
                        name: DATA.ITEMS.SAGE_INVOICES.values
                    });
                this.SET_SAGE_LOADER(link, category, {
                    loading: false,
                    lan: link,
                    page: payload.page,
                    pageSize: payload.pageSize,
                    processed: payload.processed,
                    totalCount: res?.body?.data?.totalCount,
                });
                return res;
            } catch (error) {
                this.SET_SAGE_LOADER(link, category, { loading: false });
                this.error({
                    error: error,
                    from: 'DOWNLOAD_SAGE_INVOICES',
                    page: NAME
                });
                return error
            }
        },
        async DOWNLOAD_SAGE_TRANSFERS(payload) {
            const link = payload.lan || "";
            const category = 'sage_inventory_transfer';
            try {
                this.SET_SAGE_LOADER(link, category, { loading: true });
                const res = await store.dispatch('http_get_sage_items',
                    {
                        link: link + "WarehouseTransfer/paged",
                        page: payload.page || 1,
                        pageSize: payload.pageSize || 25,
                        processed: payload.processed,
                        name: DATA.ITEMS.SAGE_TRANSFERS.values
                    });
                this.SET_SAGE_LOADER(link, category, {
                    loading: false,
                    lan: link,
                    page: payload.page,
                    pageSize: payload.pageSize,
                    processed: payload.processed,
                    totalCount: res?.body?.data?.totalCount,
                });
                return res
            } catch (error) {
                this.SET_SAGE_LOADER(link, category, { loading: false });
                this.error({
                    error: error,
                    from: 'DOWNLOAD_SAGE_TRANSFERS',
                    page: NAME
                });
                return error
            }
        },
        async DOWNLOAD_SAGE_CUSTOMERS(payload) {
            const link = payload.lan;
            const category = 'sage_customer';
            try {
                this.SET_SAGE_LOADER(link, category, { loading: true });
                const res = await store.dispatch('http_get_sage_items',
                    {
                        link: link + "CustomerMaster/all",
                        name: DATA.ITEMS.SAGE_CUSTOMERS.values
                    });
                this.SET_SAGE_LOADER(link, category, {
                    loading: false,
                    lan: link,
                    totalCount: Array.isArray(res?.data) ? res.data.length : this.GET_SAGE_LOADER(link, category).totalCount,
                });
                this.ON_UPDATE_CUSTOMER_WITH_SAGE(res?.data);
                return res;

            } catch (error) {
                this.SET_SAGE_LOADER(link, category, { loading: false });
                this.error({
                    error: error,
                    from: 'DOWNLOAD_SAGE_CUSTOMERS',
                    page: NAME
                });
            }
        },
        async UPDATE_SAGE_INVOICE(payload) {
            const link = payload.lan;
            const category = 'sage_invoice';
            try {
                this.SET_SAGE_LOADER(link, category, { loading: true });
                const res = await store.dispatch('http_request',
                    {
                        data: payload,
                        path: payload.lan + "Invoice/headers/" + payload.invoiceNo?.trim() + "/process",
                        is_full_path: true,
                        name: DATA.ITEMS.SAGE_INVOICES.values
                    });
                this.DOWNLOAD_SAGE_INVOICES(this.GET_SAGE_LOADER(link, category))

            } catch (error) {
                this.error({
                    error: error,
                    from: 'UPDATE_SAGE_INVOICE',
                    page: NAME
                });
            }
        },
        async UPDATE_SAGE_INVOICES(payload) {
            const link = payload.lan;
            const category = 'sage_invoice';
            try {
                this.SET_SAGE_LOADER(link, category, { loading: true });
                const res = await store.dispatch('http_request',
                    {
                        data: payload.invoices,
                        path: payload.lan + "Invoice/headers/process-bulk",
                        is_full_path: true,
                        name: DATA.ITEMS.SAGE_INVOICES.values
                    });
                this.DOWNLOAD_SAGE_INVOICES(this.GET_SAGE_LOADER(link, category))

            } catch (error) {
                this.error({
                    error: error,
                    from: 'UPDATE_SAGE_INVOICE',
                    page: NAME
                });
            }
        },
        async UPDATE_SAGE_TRANSFERS(payload) {
            const link = payload.lan;
            const category = 'sage_inventory_transfer';
            try {
                this.SET_SAGE_LOADER(link, category, { loading: true });
                const res = await store.dispatch('http_request',
                    {
                        data: payload.orders,
                        path: payload.lan + "WarehouseTransfer/process-bulk",
                        is_full_path: true,
                        name: DATA.ITEMS.SAGE_TRANSFERS.values
                    });
                this.DOWNLOAD_SAGE_TRANSFERS(this.GET_SAGE_LOADER(link, category))
                return res

            } catch (error) {
                this.error({
                    error: error,
                    from: 'UPDATE_SAGE_TRANSFERS',
                    page: NAME
                });
                return { ...error }
            }
        },
        async UPDATE_SAGE_TRANSFER(payload) {
            const link = payload.lan;
            const category = 'sage_inventory_transfer';
            try {
                this.SET_SAGE_LOADER(link, category, { loading: true });
                const res = await store.dispatch('http_request',
                    {
                        data: payload,
                        path: payload.lan + "WarehouseTransfer/" + payload.docNum?.trim() + "/process",
                        is_full_path: true,
                        name: DATA.ITEMS.SAGE_TRANSFERS.values
                    });
                this.DOWNLOAD_SAGE_TRANSFERS(this.GET_SAGE_LOADER(link, category))
                return res
            } catch (error) {
                return { ...error }
            }
        },

        error(error) {
            try {
                if (!error) { return }
                let err = error.error ? error.error : '...'
                let from = error.from ? error.from : '...'
                let page = error.page ? error.page : '...'
                store.dispatch('ERROR', {
                    error: err,
                    from: from,
                    page: page
                })
            } catch (error) {
                this.error({
                    error: error,
                    from: 'error',
                    page: NAME,
                })
            }
        },

        logError(error, from) {
            this.error({ error, from, page: NAME });
        }
    },
    beforeDestroy() {
        Object.keys(this.SERVERS).forEach(code => this.CLOSE_SERVER(this.SERVERS[code]));
    },
    watch: {
        // LAN and EVENTS are pushed to the store directly inside START_LAN /
        // updateStatus as they change, so no extra watchers are needed here.
    }
});